import React from "react";
import { Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";

const Experience = () => {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up("md"));

    return (
        <a id="experience">
            <Box sx={{ p: 1, pt: 5 }}>
                <Typography
                    variant="h3"
                    sx={{
                        color: "#5ce2e2",
                        mt: 5,
                        mb: 5,
                        fontWeight: "bold",
                        textShadow:
                            "1px 1px 1px #919191, 1px 2px 1px #919191,1px 3px 1px #919191",
                    }}
                >
                    Work Experience
                </Typography>
                <Box sx={{ textAlign: "left" }}>
                    <Timeline position={matches ? "alternate" : "right"}>
                        <TimelineItem>
                            <TimelineOppositeContent
                                display={matches ? "flex" : "none"}
                            />
                            <TimelineSeparator>
                                <TimelineDot
                                    variant="outlined"
                                    sx={{ bgcolor: "#5ce2e2" }}
                                />
                                <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent>
                                <Typography variant="h6" component="span">
                                    DevOps Engineer / Platform Engineer <br />
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    component="span"
                                    sx={{
                                        color: "#00b0ba",
                                        fontWeight: "bold",
                                    }}
                                >
                                    <div>ANM<div></div>Remote | Jun 2023 - Present</div>
                                </Typography>
                                <ul style={{ textAlign: "left" }}>
                                    <li>
                                        Implementing migration of enterprise monitoring platform from LogicMonitor to ScienceLogic SL1, translating and re-engineering custom Dynamic Applications and integrations to maintain functional parity across managed services environments.
                                    </li>
                                    <li>
                                        Owned ServiceNow-side development for monitoring integrations, implementing platform enhancements and integration logic supporting LogicMonitor and ScienceLogic environments.
                                    </li>
                                    <li>
                                        Developed and deployed custom ServiceNow platform enhancements supporting operational workflows, asset management processes, and internal automation.
                                    </li>
                                    <li>
                                        Established structured release management and User Acceptance Testing (UAT) processes for ServiceNow development, including test documentation standards and change governance workflows.
                                    </li>
                                    <li>
                                        Developed and implemented a time-tracking feature within ServiceNow, designing data structures and automation logic to support evolving operational reporting requirements.
                                    </li>
                                    <li>
                                        Built and enhanced Splunk dashboards including the Device Upgrade Tracker, Devices by Company dashboard, and Inventory Explorer to provide operational visibility into upgrade progress, inventory status, and contractual compliance.
                                    </li>
                                    <li>
                                        Created automation and alerting systems to identify CMDB data inconsistencies and improve asset tracking and renewal accuracy.
                                    </li>
                                    <li>
                                        Collaborated with contractors and cross-functional teams to deliver platform enhancements, integrations, and operational tooling across enterprise IT systems.
                                    </li>
                                    <li>
                                        Awarded ANM Excellence in Engineering (Q4 2025) for leading complex ServiceNow platform upgrades and feature releases supporting key client integrations, coordinating contractor development, and quickly becoming the ScienceLogic SL1 subject matter expert for managed services monitoring.
                                    </li>
                                </ul>
                            </TimelineContent>
                        </TimelineItem>
                        <TimelineItem>
                            <TimelineOppositeContent
                                display={matches ? "flex" : "none"}
                            />
                            <TimelineSeparator>
                                <TimelineDot
                                    variant="outlined"
                                    sx={{ bgcolor: "#5ce2e2" }}
                                />
                                <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent>
                                <Typography variant="h6" component="span">
                                    Full Stack Engineering Fellow <br />
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    component="span"
                                    sx={{
                                        color: "#00b0ba",
                                        fontWeight: "bold",
                                    }}
                                >
                                    <div>Formation<div></div>Remote | Jan 2023 - Jun 2023</div>
                                </Typography>
                                <ul style={{
                                    textAlign: "left",
                                    display: "inline-block",
                                    marginTop: "8px",
                                    marginBottom: 0,
                                    paddingLeft: "20px",
                                    maxWidth: "560px",
                                }}>
                                    <li>
                                        Completed a competitive engineering fellowship focused on computer science fundamentals, system design, scalable architecture, and collaborative development.
                                    </li>
                                    <li>
                                        Contributed production-ready improvements to a React codebase, including implementing a feedback dialog, resolving Redux loading issues, and improving server performance.
                                    </li>
                                    <li>
                                        Received the Rising Tide Award twice for peer mentorship and contributions to technical learning.
                                    </li>
                                </ul>
                            </TimelineContent>
                        </TimelineItem>
                        <TimelineItem>
                            <TimelineOppositeContent
                                display={matches ? "flex" : "none"}
                            />
                            <TimelineSeparator>
                                <TimelineDot
                                    variant="outlined"
                                    sx={{ bgcolor: "#5ce2e2" }}
                                />
                                <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent>
                                <Typography variant="h6" component="span">
                                    Math Teacher (5th & 6th Grade) <br />
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    component="span"
                                    sx={{
                                        color: "#00b0ba",
                                        fontWeight: "bold",
                                    }}
                                >
                                    <div>Renaissance Charter School at Crown Point</div>
                                    <div>Ocoee, FL | Jul 2018 - May 2021</div> 
                                </Typography>
                                <ul>
                                    <li>
                                        Developed deep familiarity with learning management systems and digital student engagement workflows through hands-on classroom technology implementation.
                                    </li>
                                    <li>
                                        Designed and delivered a fully remote learning experience using the Schoology learning management system, creating online curriculum and digital workflows to support student engagement and progress tracking.
                                    </li>
                                    <li>
                                        Built a student performance tracking tool in Excel that enabled learners to independently monitor academic progress and identify opportunities for improvement.
                                    </li>
                                    <li>
                                        Managed instructional planning and delivery for over 125 students while ensuring alignment with Florida academic standards and adapting support based on performance data.
                                    </li>
                                    <li>
                                        Collaborated with parents and staff to support individualized learning needs and maintain consistent academic progress, contributing to improved student outcomes.
                                    </li>
                                </ul>
                            </TimelineContent>
                        </TimelineItem>
                        <TimelineItem>
                            <TimelineOppositeContent
                                display={matches ? "flex" : "none"}
                            />
                            <TimelineSeparator>
                                <TimelineDot
                                    variant="outlined"
                                    sx={{ bgcolor: "#5ce2e2" }}
                                />
                                <TimelineConnector />
                            </TimelineSeparator>
                            <TimelineContent>
                                <Typography variant="h6" component="span">
                                    Group Fitness Instructor <br />
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    component="span"
                                    sx={{
                                        color: "#00b0ba",
                                        fontWeight: "bold",
                                    }}
                                >
                                    <div>24 Hour Fitness, Castle Rock Rec Center, True Fit, Haka</div>
                                    <div>FL and CO | Jun 2013 - Nov 2018</div>
                                </Typography>
                                <ul style={{
                                    textAlign: "left",
                                    display: "inline-block",
                                    marginTop: "8px",
                                    marginBottom: 0,
                                    paddingLeft: "20px",
                                    maxWidth: "560px",
                                }}>
                                    <li>
                                        Created and moderated the 24 Hour
                                        Fitness Central Florida Instructors
                                        facebook group to foster communication
                                        between GX instructors and ease the
                                        process of finding subs
                                    </li>
                                    <li>
                                        Taught a Dance And Sculpt class of my
                                        own creation that grew much interest and
                                        participation at Castle Rock Rec Center
                                    </li>
                                    <li>
                                        Co-created a week-long fitness camp for
                                        kids ages 3 - 12, and organized a
                                        National Dance Day event at Haka! Group
                                        Fitness.
                                    </li>
                                </ul>
                            </TimelineContent>
                        </TimelineItem>
                    </Timeline>
                </Box>
            </Box>
        </a>
    );
};

export default Experience;
